import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import {
  ColombiaService,
  RegistroVitaTx,
} from '../../services/colombia.service';
import {
  VitaRegistroDetalleComponent,
  VitaRegistroDetalleResult,
} from './detalle-dialog/vita-registro-detalle.component';

/**
 * Registro Vita (operador): saldo USD de la bolsa + transacciones del rail
 * USDT→COP, con estado coordinado con Vita. Estilos según REFACTOR-ESTILOS.md
 * (mat-table + paginador + filtros, badges getStatusBg/getStatusColor, cards
 * acordeón en mobile).
 */
@Component({
  selector: 'app-vita-registro',
  templateUrl: './vita-registro.component.html',
  styleUrls: ['./vita-registro.component.sass'],
})
export class VitaRegistroComponent implements OnInit, AfterViewInit {
  loading = true;
  error: string | null = null;
  saldoUsd = 0;

  dataSource = new MatTableDataSource<RegistroVitaTx>([]);
  displayedColumns = [
    'txId',
    'fecha',
    'ruta',
    'remitente',
    'beneficiario',
    'envia',
    'recibe',
    'tasa',
    'estado',
    'acciones',
  ];
  pageSizeOptions = [10, 20, 50, 100];

  // Filtros
  filtroTexto = '';
  filtroEstado = '';
  readonly estados = [
    { value: '', label: 'Todos' },
    { value: 'PENDING', label: 'En proceso' },
    { value: 'SENT', label: 'Enviada a Vita' },
    { value: 'COMPLETED', label: 'Completada' },
    { value: 'FAILED', label: 'Rechazada' },
    { value: 'REQUIRES_HUMAN_REVIEW', label: 'En revisión' },
  ];

  /** Acordeón mobile: una card expandida a la vez. */
  expandedCardId: number | null = null;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(private svc: ColombiaService, private dialog: MatDialog) {}

  openDetalle(row: RegistroVitaTx): void {
    const isMobile = window.innerWidth < 768;
    const ref = this.dialog.open(VitaRegistroDetalleComponent, {
      data: { tx: row },
      maxHeight: '90vh',
      maxWidth: isMobile ? '95vw' : '600px',
      minWidth: isMobile ? '90vw' : '520px',
      panelClass: 'aguacate-dialog',
    });
    // WS3.1: si el operador resolvió (ENVIADA/RECHAZADA), refrescar la tabla
    // para que el estado y el saldo de la bolsa reflejen el cierre.
    ref.afterClosed().subscribe((r: VitaRegistroDetalleResult) => {
      if (r === 'resuelta') this.cargar();
    });
  }

  ngOnInit(): void {
    this.dataSource.filterPredicate = (row) => {
      const q = this.filtroTexto.trim().toLowerCase();
      const txtOk =
        !q ||
        `${row.txId} ${row.remitente?.nombre ?? ''} ${row.remitente?.email ?? ''} ${row.beneficiario ?? ''} ${row.vitaTxId ?? ''} ${row.destino?.pais ?? ''} ${row.origen?.moneda ?? ''}`
          .toLowerCase()
          .includes(q);
      const estOk = !this.filtroEstado || row.status === this.filtroEstado;
      return txtOk && estOk;
    };
    this.cargar();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  async cargar(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const r = await this.svc.registro();
      this.saldoUsd = r.saldoUsd ?? 0;
      this.dataSource.data = r.transacciones || [];
      this.dataSource.paginator = this.paginator;
      this.aplicarFiltros();
    } catch (e) {
      this.error = 'No se pudo cargar el registro de Vita.';
    } finally {
      this.loading = false;
    }
  }

  aplicarFiltros(): void {
    // Trigger del filterPredicate (lee filtroTexto/filtroEstado).
    this.dataSource.filter = `${this.filtroTexto}|${this.filtroEstado}|${Math.random()}`;
    if (this.dataSource.paginator) this.dataSource.paginator.firstPage();
  }

  get total(): number {
    return this.dataSource.data.length;
  }

  toggleCard(id: number): void {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  /** Etiqueta legible del estado coordinado con Vita. */
  estadoLabel(s: string): string {
    switch (s) {
      case 'PENDING':
      case 'PROCESSING':
        return 'En proceso';
      case 'SENT':
        return 'Enviada a Vita';
      case 'COMPLETED':
        return 'Completada';
      case 'FAILED':
        return 'Rechazada';
      case 'REQUIRES_HUMAN_REVIEW':
        return 'En revisión';
      case 'ANULADA':
        return 'Anulada';
      default:
        return s;
    }
  }

  /** Estado a mostrar: un FAILED cuya transacción quedó ANULADA se muestra «Anulada» (25/10/2026). */
  estadoDe(row: RegistroVitaTx): string {
    return row?.status === 'FAILED' && row?.txEstado === 'ANULADA' ? 'ANULADA' : row?.status;
  }

  getStatusBg(row: RegistroVitaTx): string {
    if (this.estadoDe(row) === 'ANULADA') return '#6b7280';
    switch (row?.status) {
      case 'COMPLETED':
        return '#15803d';
      case 'SENT':
        return '#16a34a';
      case 'FAILED':
        return '#dc2626';
      case 'PENDING':
      case 'PROCESSING':
        return '#f59e0b';
      case 'REQUIRES_HUMAN_REVIEW':
        return '#8b5cf6';
      default:
        return '#f3f4f6';
    }
  }

  getStatusColor(row: RegistroVitaTx): string {
    return this.getStatusBg(row) === '#f3f4f6' ? '#6b7280' : '#ffffff';
  }

  // ── Moneda de destino por fila (multi-rail: COP entero / PEN 2 dec.) ──
  /** Monto que recibe el destinatario, en su moneda (fallback a recibeCop/COP). */
  recibeDe(t: RegistroVitaTx): number {
    return t.recibe != null ? t.recibe : t.recibeCop;
  }
  /** Moneda de destino de la fila (fallback COP). */
  monedaDe(t: RegistroVitaTx): string {
    return (t.moneda || t.destino?.moneda || 'COP') as string;
  }
  /** digitsInfo del pipe number según los decimales de la moneda (COP=0, PEN=2). */
  fmtDe(t: RegistroVitaTx): string {
    return (t.decimales ?? 0) >= 2 ? '1.2-2' : '1.0-0';
  }

  // ── Columna "Envía" (multi-origen: el cliente paga en su moneda de origen) ──
  /** true si el cliente pagó en una moneda FIAT de origen (CLP/VES), no en USDT. */
  origenEsFiat(t: RegistroVitaTx): boolean {
    const m = (t.origen?.moneda || '').toUpperCase();
    return m === 'CLP' || m === 'VES';
  }
  /** Monto que ENVIÓ el cliente, en su moneda (VES/CLP → origenMonto; USDT → montoUsd). */
  enviaMonto(t: RegistroVitaTx): number {
    return this.origenEsFiat(t) ? (t.montoOrigen ?? 0) : t.montoUsd;
  }
  /** Moneda con la que pagó el cliente (VES/CLP/USD). */
  enviaMoneda(t: RegistroVitaTx): string {
    return this.origenEsFiat(t) ? (t.origen?.moneda || '') : 'USD';
  }
  /** digitsInfo del monto enviado (VES/CLP enteros; USD 2 dec.). */
  enviaFmt(t: RegistroVitaTx): string {
    return this.origenEsFiat(t) ? '1.0-0' : '1.2-2';
  }
  /** Tasa única que ve el cliente (dest/origen). VES/CLP → tasaCliente; USDT → tasaEfectiva. */
  tasaValor(t: RegistroVitaTx): number {
    return this.origenEsFiat(t) ? (t.tasaCliente ?? 0) : (t.tasaEfectiva ?? 0);
  }
  /** digitsInfo de la tasa cliente: PEN/VES muy chica (6 dec) / COP/VES (4) / CLP (2) / USD dest. */
  tasaFmt(t: RegistroVitaTx): string {
    const origen = (t.origen?.moneda || '').toUpperCase();
    if (origen === 'VES') return this.monedaDe(t) === 'PEN' ? '1.4-6' : '1.4-4';
    if (origen === 'CLP') return '1.2-2';
    return this.fmtDe(t);
  }
}
